import { MutationCache, QueryCache, QueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { HttpError } from "./http-error";
import { useCopyToClipboard } from "@uidotdev/usehooks";
import { Button } from "@/components/ui/button";
import { CheckIcon, CopyIcon } from "lucide-react";

type UseErrorToastOptions = {
  error: Error;
};

export function showErrorToast({ error }: UseErrorToastOptions) {
  if (error instanceof HttpError) {
    toast.error(<ToastWithTraceId httpError={error} />, {
      duration: 60000,
    });
  } else {
    toast.error(error.message || "An unexpected error occurred");
  }
}

// Create a client
export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => showErrorToast({ error }),
  }),
  mutationCache: new MutationCache({
    onError: (error) => showErrorToast({ error }),
  }),
});

export function ToastWithTraceId({ httpError }: { httpError: HttpError }) {
  const [copied, copyToClipboard] = useCopyToClipboard();
  const { traceId, message } = httpError;

  const handleCopyTraceId = () => {
    if (!traceId) return;
    copyToClipboard(traceId);
  };
  return (
    <div className="flex flex-col gap-1 w-full">
      <div className="flex font-medium items-center gap-2">
        An error occurred
      </div>
      <p className="text-foreground/70 break-words overflow-wrap-anywhere">
        {message}
      </p>
      {traceId && (
        <Button
          variant="link"
          size="sm"
          onClick={handleCopyTraceId}
          className="cursor-pointer px-0 py-0 gap-1.5 text-xs text-primary/70 self-end"
        >
          {copied ? (
            <CheckIcon className="size-3.5" />
          ) : (
            <CopyIcon className="size-3.5" />
          )}
          Copy Request ID
        </Button>
      )}
    </div>
  );
}
